// =============================================================================
// EXPLORATION — NOT FOR DEPLOYMENT
//
// The FTUE wired up under `frontend/src/app/ftue/flows/` is a design
// exploration. It exists to inform the shape of the real onboarding flow;
// it is not a finished feature.
//
//   • Copy, step order, and advance triggers are draft and may change.
//   • There is no audience targeting, A/B harness, or feature-flag gate.
//   • State persistence is browser-local (`localStorage`), not server-backed.
//   • Test coverage covers the kit primitives only, not this flow's behaviour.
//
// Before any production rollout, replace or remove the flow under
// `flows/`. The kit at `frontend/src/app/components/ui/coachmark/` and the
// shared persistence + analytics layers are intended to survive; the
// cold-list flow files should be treated as throwaway.
// =============================================================================

import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { ColdListFtueMount } from './flows/coldList'

const FTUE_STORAGE_PREFIX = 'credem.ftue.'
const FTUE_ENABLED = false

/**
 * Single mount point for every FTUE flow active on the dashboard. The
 * shared `CoachmarkProvider` is mounted higher up (in `AppShell`); this
 * component is where individual flows register themselves and render
 * their coachmarks / prompt strips.
 *
 * Removing one flow is a single import deletion + the matching JSX
 * element. The kit and other flows stay untouched.
 *
 * Supports `?ftue=reset` in the URL: wipes every `credem.ftue.*`
 * localStorage entry on the FIRST render (before children's effects
 * register flows with hydrated state), then strips the query param so
 * the URL stays clean. Convenience for QA — saves a DevTools snippet.
 *
 * STATUS: exploration. See the file header at the top of this module.
 */
export function FtueRoot() {
  return FTUE_ENABLED ? <ActiveFtueRoot /> : null
}

function ActiveFtueRoot() {
  useFtueResetParam()
  return (
    <>
      <ColdListFtueMount />
    </>
  )
}

function useFtueResetParam(): void {
  const location = useLocation()
  const navigate = useNavigate()
  // Synchronous one-shot during render — must clear storage BEFORE any
  // child flow's useEffect calls registerFlow, otherwise hydrate reads
  // stale state and the reset has no effect this session.
  const consumedRef = useRef(false)

  if (!consumedRef.current && typeof window !== 'undefined') {
    const params = new URLSearchParams(location.search)
    if (params.get('ftue') === 'reset') {
      try {
        const keys = Object.keys(window.localStorage)
        for (const key of keys) {
          if (key.startsWith(FTUE_STORAGE_PREFIX)) {
            window.localStorage.removeItem(key)
          }
        }
      } catch {
        // localStorage may be unavailable (private mode); fail silently.
      }
      consumedRef.current = true
    }
  }

  useEffect(() => {
    if (!consumedRef.current) return
    const params = new URLSearchParams(location.search)
    if (!params.has('ftue')) return
    params.delete('ftue')
    const nextSearch = params.toString()
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
      { replace: true },
    )
  }, [location.pathname, location.search, navigate])
}
